// src/pages/providers/2_dashboard/components/RecentlyViewedDropdown.js
//
// "Recently viewed" popover for the clinician dashboard. Shows up to 5
// patients the clinician recently opened, most-recent first, with a relative
// timestamp. Clicking an entry navigates to that patient's overview page
// (Adult ADHD, Adult Autism, or CYP ADHD — module-aware via the same
// getPatientOverviewPath helper the dashboard tables use).
//
// Sits right-aligned in the Quick Filter chips row on the Current Patients
// tab. The trigger mirrors the Search & Filter button's ghost styling so
// the two controls read as a pair. When no patients have been viewed yet
// the popover shows a friendly empty-state message.
//
// UI pattern: Radix Popover (same primitive as the dashboard's Search &
// Filter panel), not DropdownMenu — Popover gives us full layout control
// for the richer content (icon + name + timestamp per row) and avoids
// relying on @radix-ui/react-dropdown-menu which is only a transitive dep.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { ClockIcon } from '@heroicons/react/outline';
import { UserIcon } from '@heroicons/react/outline';
import {
  getPatientOverviewPath,
  getPatientOverviewNavState,
} from '../utils/patientOverviewRoute';

// ---------------------------------------------------------------------------
// Relative time helper
// ---------------------------------------------------------------------------

/**
 * Formats a Unix-ms timestamp as a human-readable relative time string.
 *
 * Returns short, scannable labels: "Just now" for anything under a minute,
 * then "X min ago" / "X hr ago" / "Yesterday" / "X days ago". Caps at
 * "Earlier this session" for anything over 7 days (sessionStorage is tab-
 * scoped, so entries older than a day are already unusual).
 *
 * @param {number} timestampMs  Unix milliseconds (from Date.now())
 * @returns {string}
 */
export function formatRelativeTime(timestampMs) {
  const diffMs = Date.now() - timestampMs;

  if (diffMs < 0) return 'Just now';

  const MINUTE = 60 * 1000;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  if (diffMs < MINUTE) return 'Just now';
  if (diffMs < HOUR) {
    const mins = Math.floor(diffMs / MINUTE);
    return `${mins} min ago`;
  }
  if (diffMs < DAY) {
    const hrs = Math.floor(diffMs / HOUR);
    return `${hrs} hr ago`;
  }
  if (diffMs < 2 * DAY) return 'Yesterday';
  if (diffMs < 7 * DAY) {
    const days = Math.floor(diffMs / DAY);
    return `${days} days ago`;
  }

  return 'Earlier this session';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Recently-viewed patients popover trigger + content.
 *
 * @param {object}   props
 * @param {Array}    props.recentPatients      Ordered list from useRecentlyViewedPatients.
 * @param {function} [props.onRecordPatientView] Optional callback to record a view when a
 *   patient is opened from this dropdown. Without this, re-opening a patient
 *   from the dropdown would not promote it to position 0 in the list.
 */
export default function RecentlyViewedDropdown({ recentPatients, onRecordPatientView }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Navigate to a patient's overview and close the popover. Uses the same
  // path resolution and nav state as the dashboard table name-link and
  // View Patient button, so all three entry points are consistent.
  // Also re-records the view so the patient is promoted to position 0 in
  // the recently-viewed list — without this, clicking an older entry from
  // the dropdown would not update its relative position or timestamp.
  const handlePatientClick = (entry) => {
    const path = getPatientOverviewPath(entry);
    if (!path) return;

    onRecordPatientView?.(entry);
    const state = getPatientOverviewNavState(entry);
    navigate(path, { state });
    setIsOpen(false);
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <button
          className="flex flex-row items-center justify-center p-2 rounded-xl transition-colors duration-200 hover:bg-gray-100 flex-shrink-0"
          aria-label="Recently viewed patients"
        >
          <ClockIcon className="w-5 h-5 mr-1 text-gray-600" />
          <span className="text-gray-600 text-xs font-medium whitespace-nowrap">Recently viewed</span>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="bg-white border border-gray-300 rounded-lg shadow-lg p-2 w-[280px] animate-in fade-in zoom-in-95 duration-200 z-50"
          sideOffset={5}
          align="end"
        >
          {recentPatients.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-3">
              No recently viewed patients
            </p>
          ) : (
            <ul className="flex flex-col" role="list">
              {recentPatients.map((entry) => (
                <li key={entry.patientId}>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-gray-50 transition-colors duration-150 group"
                    onClick={() => handlePatientClick(entry)}
                    aria-label={`View ${entry.firstName} ${entry.lastName}`}
                  >
                    {/* Patient icon — matches the dashboard table row styling */}
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center">
                      <UserIcon className="h-4 w-4 text-violet-800" />
                    </div>

                    {/* Name and relative time */}
                    <div className="min-w-0 flex-1">
                      <span className="block text-xs font-semibold text-slate-800 truncate group-hover:text-primary transition-colors duration-150">
                        {entry.firstName} {entry.lastName}
                      </span>
                      <span className="block text-[11px] text-gray-400">
                        {formatRelativeTime(entry.viewedAt)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
